import { useEffect, useMemo, useState } from 'react';

import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useEventBus } from '../../../../hooks/use-event-bus';
import { ContactApi } from '../../../../services/api-client/contact-api';
import FormsService from '../../../../services/forms-service/forms-service';
import { PatientDto } from '../../../../services/forms-service/forms-service-types';
import { CircularProgress } from '../../../shared/audio-player/styles';
import { CenterContent, Dialog } from './styles';

export default function ModalRemoveAllAssignedForms() {
  const [open, setOpen] = useState(false);
  const [patient, setPatient] = useState<PatientDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { listen, publish } = useEventBus();
  const { showSuccess, showError } = useNotification();

  const { config } = useConfig();
  const oidc = useOidcWithAdmin();

  const formsService = useMemo(() => FormsService.getInstance(config, oidc.oidcService), [config, oidc.oidcService]);
  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  const initiateRemoveAllAssignedForms = async (patientId?: number, patientData?: PatientDto) => {
    setIsLoading(true);
    try {
      const patient = patientData || (await contactApi.getContactDetailsById(patientId!));
      setPatient(patient);
    } catch (error) {
      setOpen(false);
      showError('Failed to load patient data');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    return listen('formsDialog', (event: any) => {
      const data = event.data as { type: string; patientId?: number; patient?: PatientDto };

      if (data.type === 'remove-all-assigned-forms') {
        setPatient(null);
        setOpen(true);
        initiateRemoveAllAssignedForms(data.patientId, data.patient);
      }
    });
  }, [listen]);

  const handleClose = () => {
    setOpen(false);
  };

  const submit = async () => {
    setOpen(false);
    try {
      await formsService.unassignAllForms(patient!.id as number);
      publish({ messageType: 'forms', data: { type: 'reload' } });
      showSuccess('All forms have been unassigned');
    } catch (error) {
      showError('Failed to unassign all forms');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      {isLoading ? (
        <DialogContent>
          <CenterContent>
            <CircularProgress />
          </CenterContent>
        </DialogContent>
      ) : (
        <>
          <DialogTitle>
            Unassign All Forms from {patient?.firstName} {patient?.lastName}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>Are you sure you want to remove all assignments from this patient?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" color="error" onClick={submit}>
              Unassign All
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
